import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../../api/client";

export interface DomicilioValores {
  calle: string;
  numero: string;
  codigoPostal: string;
  colonia: string;
  ciudad: string;
  estado: string;
  pais: string;
}

interface Props {
  valores: DomicilioValores;
  onChange: (campo: keyof DomicilioValores, valor: string) => void;
  disabled?: boolean;
  // Mensajes de error por campo (opcional). Cuando se usa este formulario en
  // una alta donde todo el domicilio es obligatorio, se marca en rojo el
  // campo que falta y se muestra el mensaje debajo — el resto de las
  // pantallas que ya usaban este componente no se ven afectadas si no
  // mandan esta prop.
  errores?: Partial<Record<keyof DomicilioValores, string>>;
}

// Domicilio por partes con autocompletado de colonia/ciudad/estado a partir
// del código postal (API de COPOMEX, vía el backend). Si el código postal no
// se reconoce, si la API no está configurada, o si falla la consulta, no
// pasa nada: el negocio simplemente sigue llenando colonia/ciudad/estado a
// mano, como cualquier campo de texto normal.
export function DomicilioCampos({ valores, onChange, disabled, errores }: Props) {
  const [colonias, setColonias] = useState<string[]>([]);
  const [buscando, setBuscando] = useState(false);
  const idDatalist = useRef(`colonias-${Math.random().toString(36).slice(2)}`).current;
  const ultimoCpBuscado = useRef<string>("");

  useEffect(() => {
    const cp = valores.codigoPostal;
    if (!/^\d{5}$/.test(cp) || cp === ultimoCpBuscado.current) return;

    const timeout = setTimeout(async () => {
      ultimoCpBuscado.current = cp;
      setBuscando(true);
      try {
        const res = await api.get(`/utilidades/codigo-postal/${cp}`);
        if (res.data?.disponible) {
          setColonias(res.data.colonias ?? []);
          if (res.data.ciudad) onChange("ciudad", res.data.ciudad);
          if (res.data.estado) onChange("estado", res.data.estado);
        } else {
          setColonias([]);
        }
      } catch {
        setColonias([]);
      } finally {
        setBuscando(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, 400);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valores.codigoPostal]);

  function clase(campo: keyof DomicilioValores) {
    return errores?.[campo] ? "input border-red-400 focus:ring-red-300" : "input";
  }
  function ErrorTexto({ campo }: { campo: keyof DomicilioValores }) {
    if (!errores?.[campo]) return null;
    return <p className="mt-1 text-xs text-red-600">{errores[campo]}</p>;
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2 sm:col-span-1" data-campo="calle">
        <label className="label">Calle</label>
        <input
          className={clase("calle")}
          disabled={disabled}
          value={valores.calle}
          onChange={(e) => onChange("calle", e.target.value)}
        />
        <ErrorTexto campo="calle" />
      </div>
      <div data-campo="numero">
        <label className="label">Número</label>
        <input
          className={clase("numero")}
          disabled={disabled}
          value={valores.numero}
          onChange={(e) => onChange("numero", e.target.value)}
        />
        <ErrorTexto campo="numero" />
      </div>
      <div data-campo="codigoPostal">
        <label className="label flex items-center gap-1.5">
          Código postal
          {buscando && <Loader2 size={12} className="animate-spin text-slate-400" />}
        </label>
        <input
          className={clase("codigoPostal")}
          disabled={disabled}
          inputMode="numeric"
          maxLength={5}
          placeholder="Ej. 06700"
          value={valores.codigoPostal}
          onChange={(e) => onChange("codigoPostal", e.target.value.replace(/\D/g, "").slice(0, 5))}
        />
        <ErrorTexto campo="codigoPostal" />
      </div>
      <div data-campo="colonia">
        <label className="label">Colonia</label>
        <input
          className={clase("colonia")}
          disabled={disabled}
          list={idDatalist}
          placeholder={colonias.length ? "Selecciona o escribe la tuya" : "Escribe tu colonia"}
          value={valores.colonia}
          onChange={(e) => onChange("colonia", e.target.value)}
        />
        <datalist id={idDatalist}>
          {colonias.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <ErrorTexto campo="colonia" />
      </div>
      <div data-campo="ciudad">
        <label className="label">Ciudad / municipio</label>
        <input
          className={clase("ciudad")}
          disabled={disabled}
          value={valores.ciudad}
          onChange={(e) => onChange("ciudad", e.target.value)}
        />
        <ErrorTexto campo="ciudad" />
      </div>
      <div data-campo="estado">
        <label className="label">Estado</label>
        <input
          className={clase("estado")}
          disabled={disabled}
          value={valores.estado}
          onChange={(e) => onChange("estado", e.target.value)}
        />
        <ErrorTexto campo="estado" />
      </div>
      <div data-campo="pais">
        <label className="label">País</label>
        <input
          className={clase("pais")}
          disabled={disabled}
          value={valores.pais}
          onChange={(e) => onChange("pais", e.target.value)}
        />
        <ErrorTexto campo="pais" />
      </div>
    </div>
  );
}
